import { Dialog } from "primereact/dialog"
import { useEffect, useState } from "react"
import { useDispatch } from "react-redux"

import Loading from "../../../common/Loading"

import { Button, DropDownField, TextAreaField, TextInputField } from "../../../helpers"

import { getStockWarehouses } from "../../../services/api/stockWarehouse"
import { searchSmartStockMoves } from "../../../services/api/smartStockMoves"
import { insertStockAdjustment } from "../../../services/api/stockAdjustment"

import { raiseToast } from "../../../services/redux/reducers/toast/toast"

import { validateInputs } from "../../../utils"

import { stockTransferInitialValues, stockTransferValidationSchema } from "../../../validation/stockAdjustment"


const StockTransfer = (props) => {

    const { companyWarehouses, setVisibleStockTransfer } = props

    const [stockTransfer, setStockTransfer] = useState(stockTransferInitialValues)
    const [smartStockMoves, setSmartStockMoves] = useState([])
    const [stockWarehouse, setStockWarehouse] = useState(null)

    const [formErrors, setFormErrors] = useState([])

    const [isLoading, setIsLoading] = useState(false)

    const dispatch = useDispatch()

    useEffect(() => {
        setStockTransfer({ ...stockTransfer, targetWarehouseId: null, remarks: null })
        setStockWarehouse(null)
        setSmartStockMoves([])
    }, [stockTransfer.materialId, stockTransfer.currentWarehouseId])

    const handleOnHideStockTransfer = () => {
        setSmartStockMoves([])
        setStockTransfer(stockTransferInitialValues)
        setVisibleStockTransfer(false)
    }

    const handleStockDetailsClick = async () => {
        if (!stockTransfer.materialId) {
            return dispatch(raiseToast({ severity: 'warn', detail: 'Please enter material ID!', life: 3000 }))
        }

        if (stockTransfer.materialId.length <= 5) {
            return dispatch(raiseToast({ severity: 'warn', detail: 'The length of material ID is at least 5!', life: 3000 }))
        }

        if (!stockTransfer.currentWarehouseId) {
            return dispatch(raiseToast({ severity: 'warn', detail: 'Please enter warehouse ID!', life: 3000 }))
        }

        setIsLoading(true)

        const stockWarehouseRes = await getStockWarehouses({ materialIds: [parseInt(stockTransfer.materialId)], warehouseIds: [stockTransfer.currentWarehouseId], statusIds: [1], paginator: false })
        if (stockWarehouseRes.failure) {
            setIsLoading(false)
            return dispatch(raiseToast({ severity: 'error', summary: "Stock warehouse", detail: stockWarehouseRes.data.error, life: 3000 }))
        }

        if (stockWarehouseRes.data.data.length) {
            setStockWarehouse(stockWarehouseRes.data.data[0])

            const smartStockMovesRes = await searchSmartStockMoves({ materialIds: [parseInt(stockTransfer.materialId)], warehouseIds: [stockTransfer.currentWarehouseId], statusIds: [1], paginator: false })
            if (smartStockMovesRes.failure) {
                setIsLoading(false)
                return dispatch(raiseToast({ severity: 'error', summary: "Smart Stock Moves", detail: smartStockMovesRes.data.error, life: 3000 }))
            }

            const updatedSmartStockMoves = smartStockMovesRes.data.data.map(eachSmartStockMove => { return { ...eachSmartStockMove, adjustedQty: null } })
            setSmartStockMoves(updatedSmartStockMoves)
            setIsLoading(false)
            setStockTransfer({...stockTransferInitialValues, materialId: stockTransfer.materialId, currentWarehouseId: stockTransfer.currentWarehouseId})
        }
        else {
            setIsLoading(false)
            return dispatch(raiseToast({ severity: 'error', detail: 'Stock not found!', life: 3000 }))
        }
    }

    const handleStockTransferChange = (e) => {
        const updatedStockTransfer = { ...stockTransfer, [e.target.name]: e.target.value }
        setStockTransfer(updatedStockTransfer)
    }

    const handleAdjustedQtyChange = (smartStockMoveId, e, sellerPOItemQty) => {
        let updatedValue = e.target.value
        if (parseFloat(e.target.value) > parseFloat(sellerPOItemQty)) {
            dispatch(raiseToast({ severity: 'warn', detail: `Max quantity available is ${sellerPOItemQty}`, life: 3000 }))
            updatedValue = parseFloat(sellerPOItemQty)
        }

        const updatedSmartStockMoves = smartStockMoves.map(eachSmartStockMove => {
            if (eachSmartStockMove.smartStockMoveId === smartStockMoveId) {
                return { ...eachSmartStockMove, adjustedQty: updatedValue }
            }
            return eachSmartStockMove
        })

        setSmartStockMoves([...updatedSmartStockMoves])
    }

    const handleSubmit = async () => {

        const validationRes = await validateInputs(stockTransferValidationSchema, stockTransfer)
        if (validationRes) {
            if (validationRes.error) {
                setFormErrors(validationRes.error)
            }
            else {
                setFormErrors({})

                if (!stockWarehouse) {
                    return dispatch(raiseToast({ severity: 'error', detail: `Please get stock details`, life: 3000 }))
                }

                if (smartStockMoves.length === 0) {
                    return dispatch(raiseToast({ severity: 'error', detail: `Please get stock details`, life: 3000 }))
                }

                if (stockTransfer.currentWarehouseId === stockTransfer.targetWarehouseId) {
                    return dispatch(raiseToast({ severity: 'error', detail: `Can't transfer to same warehouse!`, life: 3000 }))
                }

                const filteredSmartStockMoves = smartStockMoves.filter(eachSmartStockMove => parseFloat(eachSmartStockMove.adjustedQty) > 0)

                if (filteredSmartStockMoves.length === 0) {
                    return dispatch(raiseToast({ severity: 'error', detail: `Please enter quantity to transfer`, life: 3000 }))
                }

                const isDataNotProvided = filteredSmartStockMoves.some(eachSmartStockMove => {
                    if (!eachSmartStockMove.sellerPODocNo) {
                        dispatch(raiseToast({ severity: 'error', detail: `Please provide Seller PO Doc Number for ${stockTransfer.materialId}`, life: 3000 }))
                        return true
                    }
                    if (!eachSmartStockMove.price) {
                        dispatch(raiseToast({ severity: 'error', detail: `Please give price against Seller PO ${eachSmartStockMove.sellerPODocNo}`, life: 3000 }))
                        return true
                    }
                    if (!eachSmartStockMove.GSTRate && eachSmartStockMove.GSTRate !== 0) {
                        dispatch(raiseToast({ severity: 'error', detail: `Please give GST rate against Seller PO ${eachSmartStockMove.sellerPODocNo}`, life: 3000 }))
                        return true
                    }
                })

                if (isDataNotProvided) {
                    return
                }

                const posStockAdjustments = filteredSmartStockMoves.map(eachSmartStockMove => {
                    return {
                        materialId: parseInt(stockTransfer.materialId),
                        warehouseId: stockTransfer.currentWarehouseId,
                        isPositiveAdjustment: false,
                        sellerPODocNo: eachSmartStockMove.sellerPODocNo,
                        sellerPONo: eachSmartStockMove.sellerPODocNo,
                        unitPrice: parseFloat(eachSmartStockMove.price),
                        GSTRate: eachSmartStockMove.GSTRate,
                        adjustedQty: parseFloat(eachSmartStockMove.adjustedQty),
                        moveDate: new Date(),
                        remarks: stockTransfer.remarks
                    }
                })

                const negStockAdjustments = filteredSmartStockMoves.map(eachSmartStockMove => {
                    return {
                        materialId: parseInt(stockTransfer.materialId),
                        warehouseId: stockTransfer.targetWarehouseId,
                        isPositiveAdjustment: true,
                        sellerPODocNo: eachSmartStockMove.sellerPODocNo,
                        sellerPONo: eachSmartStockMove.sellerPODocNo,
                        unitPrice: parseFloat(eachSmartStockMove.price),
                        GSTRate: eachSmartStockMove.GSTRate,
                        adjustedQty: parseFloat(eachSmartStockMove.adjustedQty),
                        moveDate: new Date(),
                        remarks: stockTransfer.remarks
                    }
                })

                if (window.confirm("Are you sure you want to continue?")) {
                    setIsLoading(true)
                    const stockAdjustmentRes = await insertStockAdjustment({ stockAdjustments: [...posStockAdjustments, ...negStockAdjustments] })

                    if (stockAdjustmentRes.failure) {
                        setIsLoading(false)
                        return dispatch(raiseToast({ severity: 'error', summary: 'Stock Adjustment', detail: stockAdjustmentRes.data.error, life: 3000 }))
                    } else {
                        dispatch(raiseToast({ severity: 'success', detail: "Stock Transfer successful" }))
                    }
                    setIsLoading(false)
                    handleOnHideStockTransfer()
                }
            }
        }
    }

    return (
        <Dialog header="Stock Transfer" visible={props.visible} style={{ minHeight: "60%", width: '60vw', }} onHide={handleOnHideStockTransfer}>
            <Loading visible={isLoading} />
            <div className="flex flex-row">
                <div className="flex flex-column col-12 mb-2 lg:col-3 lg:mb-0">
                    <TextInputField name="materialId" labelText="Material ID" value={stockTransfer.materialId} onChange={handleStockTransferChange} errorMessage={formErrors.materialId} keyfilter={"pnum"} required maxLength={10} />
                </div>
                <div className="flex flex-column col-12 mb-2 lg:col-3 lg:mb-0">
                    <DropDownField name="currentWarehouseId" labelText="Current Warehouse" options={companyWarehouses} value={stockTransfer.currentWarehouseId} optionLabel="warehouseName" optionValue="warehouseId" onChange={handleStockTransferChange} errorMessage={formErrors.currentWarehouseId} required />
                </div>
                <div className="flex align-items-center">
                    <Button label='Get Stock Details' className='h-2rem p-2' onClick={handleStockDetailsClick} text />
                </div>
            </div>
            {
                stockWarehouse ?
                    <div className="flex align-items-center">
                        <p className="ml-2">Current stock in the warehouse: </p>
                        <div className="flex flex-column col-12 mb-2 lg:col-2 lg:mb-0">
                            <TextInputField name="currentStockQty" value={stockWarehouse?.smartStock ? stockWarehouse.smartStock : 0} errorMessage={formErrors.currentStockQty} keyfilter={"pnum"} disabled required />
                        </div>
                    </div>
                    : null
            }
            <div>
                {smartStockMoves.length ?

                    smartStockMoves.map((eachSmartStockMove, index) => {
                        let displayLabel = false
                        if (index === 0) {
                            displayLabel = true
                        }
                        return (
                            <div className="flex flex-row" key={`smartStockMoves-${eachSmartStockMove.smartStockMoveId}`}>
                                <div className="flex flex-column col-12 mb-2 lg:col-3 lg:mb-0">
                                    <TextInputField name="sellerPODocNo" labelText={displayLabel && "Sel PO Doc No"} value={eachSmartStockMove.sellerPODocNo} keyfilter={"pnum"} disabled required />
                                </div>
                                <div className="flex flex-column col-12 mb-2 lg:col-2 lg:mb-0">
                                    <TextInputField name="price" labelText={displayLabel && "Price"} value={eachSmartStockMove.price} keyfilter={"pnum"} disabled required />
                                </div>
                                <div className="flex flex-column col-12 mb-2 lg:col-2 lg:mb-0">
                                    <TextInputField name="sellerPOItemQty" labelText={displayLabel && "Sel PO Qty"} value={eachSmartStockMove.moveQty} keyfilter={"pnum"} disabled required />
                                </div>
                                <div className="flex flex-column col-12 mb-2 lg:col-2 lg:mb-0">
                                    <TextInputField name="adjustedQty" labelText={displayLabel && "Adjusted Qty"} value={eachSmartStockMove.adjustedQty} keyfilter={"pnum"} onChange={(e) => { handleAdjustedQtyChange(eachSmartStockMove.smartStockMoveId, e, eachSmartStockMove.moveQty) }} required />
                                </div>
                            </div>
                        )
                    }) : <p className="align-self-center">Get stock seller POs for the material to proceed with stock transfer</p>
                }
                {
                    stockWarehouse &&
                    <div className="flex flex-column col-12 mb-2 lg:col-3 lg:mb-0">
                        <DropDownField name="targetWarehouseId" labelText="Target Warehouse" options={companyWarehouses} value={stockTransfer.targetWarehouseId} optionLabel="warehouseName" optionValue="warehouseId" onChange={handleStockTransferChange} errorMessage={formErrors.targetWarehouseId} required />
                    </div>
                }
            </div>
            <div>
                <div className="flex flex-column col-12 text-area">
                    <TextAreaField name="remarks" labelText="Remarks" rows={3} onChange={handleStockTransferChange} value={stockTransfer.remarks} errorMessage={formErrors.remarks} required maxLength={100} />
                </div>
            </div>
            <div className='flex justify-content-center mt-2'>
                <Button label="Submit" onClick={handleSubmit} />
            </div>
        </Dialog>
    )
}
export default StockTransfer